import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Image,
  IconButton,
  Spinner,
  Box,
  HStack,
  Switch,
  VStack,
  IconButton as ChakraIconButton,
  Text,
  Heading,
  Badge,
  AspectRatio,
  useToast,
  Button,
  Icon,
  Flex,
  Tooltip,
  useColorModeValue,
  Divider,
  useBreakpointValue,
  Wrap,
  WrapItem
} from '@chakra-ui/react';
import { LazyImage } from '../utils/lazyLoadUtils';
import { ChevronLeftIcon, ChevronRightIcon, AddIcon, MinusIcon, EditIcon } from '@chakra-ui/icons';
import { MdLocationPin } from 'react-icons/md';
import { 
  ArrowsOut, 
  ArrowsIn, 
  TagSimple, 
  MapPin, 
  Info, 
  X, 
  CaretLeft, 
  CaretRight 
} from '@phosphor-icons/react';
import DetectionBoxes from './media/DetectionBoxes';
import FeatureTaggingModal from './FeatureTaggingModal';
import SubLocationMapModal from './media/SubLocationMapModal';
import { updateMediaSubLocation, updateMediaFeatures } from '../firebase/siteMedia';

export default function ElasticImagePreviewModal({
  isOpen, 
  onClose, 
  imageUrl, 
  onPrevious, 
  onNext, 
  hasMultiple, 
  currentImage, 
  onFeaturesUpdated,
  onMediaUpdated 
}) {
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(false);
  const [showSubLocationModal, setShowSubLocationModal] = useState(false);
  const toast = useToast();
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [showFeatureTagging, setShowFeatureTagging] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Responsive layout
  const iconSize = useBreakpointValue({ base: 18, md: 22 });
  const buttonSize = useBreakpointValue({ base: "sm", md: "md" });
  const navButtonSize = useBreakpointValue({ base: "md", md: "lg" });
  const modalLayout = useBreakpointValue({ base: "column", md: "row" });
  const controlsGap = useBreakpointValue({ base: 1, md: 4 });
  
  // Theme colors
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const modalBg = useColorModeValue('white', 'gray.800');
  const sidebarBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const controlsBg = useColorModeValue('whiteAlpha.800', 'blackAlpha.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const featureBg = useColorModeValue('gray.100', 'gray.700');
  const featureBgHover = useColorModeValue('gray.200', 'gray.600');

  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;
      console.log('[ElasticImagePreviewModal] handleImageLoad - naturalWidth:', naturalWidth, 'naturalHeight:', naturalHeight, 'imageUrl:', imageUrl);
      setImageSize({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight
      });
      const rect = imageRef.current.getBoundingClientRect();
      setDisplaySize({
        width: rect.width,
        height: rect.height
      });
      setIsLoading(false);
    }
  }, [imageUrl]);

  // Reset loading state when image URL changes
  useEffect(() => {
    setIsLoading(true);
  }, [imageUrl]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  }, [scale, position]);

  const handleMouseMove = useCallback((e) => {
    e.preventDefault();
    
    if (isDragging && scale > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      const maxX = (imageSize.width * scale - imageSize.width) / 2;
      const maxY = (imageSize.height * scale - imageSize.height) / 2;
      
      setPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY))
      });
    }
  }, [isDragging, scale, dragStart, imageSize]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Reset zoom and position when modal opens
      setScale(1);  
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen, imageUrl]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.5, 1));
    if (scale <= 1.5) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleClose = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    onClose();
  };

  // Normalize predictions if needed
  const normalizedPredictions = useMemo(() => {
console.log('[ElasticImagePreviewModal] Calculating normalizedPredictions. imageSize:', imageSize, 'currentImage.predictions available:', !!currentImage?.predictions, 'Number of predictions:', currentImage?.predictions?.length);
    if (currentImage?.predictions) {
      console.log('[ElasticImagePreviewModal] currentImage.predictions (first 5):', JSON.stringify(currentImage.predictions.slice(0,5), null, 2));
    }

    if (!currentImage?.predictions || currentImage.predictions.length === 0) {
      console.log('[ElasticImagePreviewModal] No predictions or empty predictions array, returning [].');
      return [];
    }

    // IMPORTANT: Only proceed with normalization if image dimensions are available
    if (imageSize.width === 0 || imageSize.height === 0) {
      console.warn('[ElasticImagePreviewModal] imageSize is zero, cannot normalize. Returning empty predictions.');
      return [];
    }
    
    // The log at original line 194 was misleading and has been removed.

    const mappedPredictions = currentImage.predictions.map((pred, index) => {
      console.log(`[ElasticImagePreviewModal] Processing pred ${index}:`, JSON.stringify(pred));

      // Check for malformed prediction object (missing essential coordinate properties or not numbers)
      if (!pred || typeof pred.x !== 'number' || typeof pred.y !== 'number' || typeof pred.width !== 'number' || typeof pred.height !== 'number') {
        console.warn(`[ElasticImagePreviewModal] Invalid or malformed prediction object at index ${index}:`, pred, 'Returning as-is with error and zeroed coordinates for inspection.');
        // Return an object that DetectionBoxes can handle, but clearly indicates an error.
        return {
          ...pred,
          x:0, y:0, width:0, height:0,
          error: "Invalid/Malformed prediction object",
          originalPred: pred // Keep original for debugging
        };
      }

      // The redundant imageSize check (original lines 197-202) has been removed as it's covered by the check at line 190.

      // Check if coordinates are already normalized (between 0-1, inclusive of 0 and 1)
      // Important: If x,y,w,h are all 0, this will be true.
      const isNormalized = pred.x >= 0 && pred.x <= 1 &&
                           pred.y >= 0 && pred.y <= 1 &&
                           pred.width >= 0 && pred.width <= 1 && // width/height can be 0
                           pred.height >= 0 && pred.height <= 1;
      
      console.log(`[ElasticImagePreviewModal] Pred ${index} - isNormalized: ${isNormalized}`, {x: pred.x, y: pred.y, width: pred.width, height: pred.height});
      
      if (isNormalized) {
        console.log(`[ElasticImagePreviewModal] Pred ${index} is already normalized or zeroed. Returning as is.`);
        return pred; // If x,y,w,h are 0,0,0,0, they will be returned as is.
      }

      // Convert pixel coordinates to normalized values
      const normalizedPred = {
        ...pred,
        x: pred.x / imageSize.width,
        y: pred.y / imageSize.height,
        width: pred.width / imageSize.width,
        height: pred.height / imageSize.height
      };
      console.log(`[ElasticImagePreviewModal] Pred ${index} normalized:`, JSON.stringify(normalizedPred));
      return normalizedPred;
    });

    console.log('[ElasticImagePreviewModal] Final mappedPredictions (first 5):', JSON.stringify(mappedPredictions.slice(0,5), null, 2));
    return mappedPredictions;
  }, [currentImage?.predictions, imageSize.width, imageSize.height]); // Ensure imageSize changes trigger re-memoization

  const handleFeatureSelect = (feature) => {
    // Don't add the feature yet, wait for bounding box
    console.log('Selected feature:', feature);
  };

  const handleFeatureBoxesDrawn = async (newPredictions) => {
    console.log('[ElasticImagePreviewModal] handleFeatureBoxesDrawn called with:', newPredictions);
    console.log('[ElasticImagePreviewModal] currentImage:', {
      siteId: currentImage?.siteId,
      id: currentImage?.id,
      url: currentImage?.url,
      type: currentImage?.type
    });

    // Enhanced validation with detailed error messages
    if (!currentImage) {
      toast({
        title: "Error",
        description: "No image data available",
        status: "error",
        duration: 3000
      });
      return;
    }

    if (!currentImage.siteId) {
      toast({
        title: "Error", 
        description: "Site ID is missing from image data. Cannot save features.",
        status: "error",
        duration: 3000
      });
      return;
    }

    if (!currentImage.id) {
      toast({
        title: "Error",
        description: "Media ID is missing from image data. This may be an issue with the search index. Please try accessing the image from the site page directly.",
        status: "error",
        duration: 5000
      });
      return;
    }
    
    setIsSaving(true);
    try {
      // Merge existing predictions with new ones
      const updatedPredictions = [
        // Keep all existing auto predictions
        ...(currentImage.predictions || []).filter(p => p.source === 'auto'),
        // Keep manual predictions that aren't being updated
        ...(currentImage.predictions || []).filter(p => 
          p.source === 'manual' && !newPredictions.some(np => np.class === p.class)
        ),
        // Add new predictions
        ...newPredictions
      ];
      
      console.log('[ElasticImagePreviewModal] Calling updateMediaFeatures with:', {
        siteId: currentImage.siteId,
        mediaId: currentImage.id,
        predictionsCount: updatedPredictions.length
      });
      
      // Update media predictions
      const result = await updateMediaFeatures(
        currentImage.siteId,
        currentImage.id, 
        updatedPredictions
      );
      
      if (result.success) {
        const updatedMedia = {
          ...currentImage,
          predictions: updatedPredictions
        };
        
        if (onFeaturesUpdated) {
          onFeaturesUpdated(updatedPredictions);
        }
        
        toast({
          title: "Features saved",
          description: "The features have been saved successfully",
          status: "success",
          duration: 3000
        });
        setShowFeatureTagging(false);
      } else {
        throw new Error(result.error || 'Failed to save features');
      }
    } catch (error) {
      console.error('Error updating features:', error);
      
      // Provide more specific error messages
      let errorMessage = "Failed to save the features";
      if (error.message.includes('Media not found')) {
        errorMessage = "The media item was not found in the database. This may be a sync issue with the search index.";
      } else if (error.message.includes('Invalid input parameters')) {
        errorMessage = "Invalid data provided. Please try again.";
      }
      
      toast({
        title: "Error saving features",
        description: errorMessage,
        status: "error",
        duration: 5000
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      size="full" 
      motionPreset="slideInBottom"
    >
      <ModalOverlay backdropFilter="blur(8px)" />
      <ModalContent
        bg={bgColor}
        m={0}
        borderRadius={0}
      >
        <Flex 
          position="absolute" 
          top={0} 
          left={0} 
          right={0} 
          p={{ base: 2, md: 3 }}
          zIndex={10} 
          bg={controlsBg} 
          backdropFilter="blur(8px)"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid"
          borderColor={borderColor}
          h="60px"
        >
          <Wrap spacing={controlsGap} align="center">
            <WrapItem>
              <HStack spacing={1}>
                <Tooltip label="Toggle feature boxes">
                  <IconButton
                    aria-label="Toggle feature boxes"
                    icon={<TagSimple weight={showBoundingBoxes ? "fill" : "regular"} />}
                    size={buttonSize}
                    colorScheme={showBoundingBoxes ? "blue" : "gray"}
                    variant={showBoundingBoxes ? "solid" : "ghost"}
                    onClick={() => setShowBoundingBoxes(!showBoundingBoxes)}
                    aria-pressed={showBoundingBoxes}
                  />
                </Tooltip>
                
                <Tooltip label={currentImage?.subLocation ? "Edit Location" : "Add Location"}>
                  <IconButton
                    aria-label={currentImage?.subLocation ? "Edit Location" : "Add Location"}
                    icon={<MapPin weight={currentImage?.subLocation ? "fill" : "regular"} />}
                    colorScheme={currentImage?.subLocation ? "green" : "gray"}
                    size={buttonSize}
                    variant={currentImage?.subLocation ? "solid" : "ghost"}
                    onClick={() => {
                      if (!currentImage?.siteId) {
                        toast({
                          title: "Error",
                          description: "Site ID is missing. Cannot add location to this image.",
                          status: "error",
                          duration: 3000
                        });
                        return;
                      }
                      setShowSubLocationModal(true);
                    }}
                  />
                </Tooltip>
                
                <Tooltip label="Edit Features">
                  <IconButton
                    aria-label="Edit Features"
                    icon={<EditIcon />}
                    colorScheme="blue"
                    size={buttonSize}
                    variant="ghost"
                    onClick={() => setShowFeatureTagging(true)}
                    isDisabled={!currentImage?.id || !currentImage?.siteId}
                  />
                </Tooltip>
              </HStack>
            </WrapItem>
            
            <WrapItem>
              <Divider orientation="vertical" height="24px" display={{ base: "none", md: "block" }} />
            </WrapItem>
            
            <WrapItem>
              <HStack spacing={1}>
                <Tooltip label="Zoom out">
                  <IconButton
                    icon={<ArrowsIn />}
                    onClick={handleZoomOut}
                    isDisabled={scale <= 1}
                    size={buttonSize}
                    colorScheme="gray"
                    variant="ghost"
                    aria-label="Zoom out"
                  />
                </Tooltip>
                
                <Text fontWeight="medium" minW="40px" textAlign="center">
                  {Math.round(scale * 100)}%
                </Text>
                
                <Tooltip label="Zoom in">
                  <IconButton
                    icon={<ArrowsOut />}
                    onClick={handleZoomIn}
                    isDisabled={scale >= 3}
                    size={buttonSize}
                    colorScheme="gray"
                    variant="ghost"
                    aria-label="Zoom in"
                  />
                </Tooltip>
              </HStack>
            </WrapItem>
          </Wrap>
          
          <IconButton
            icon={<X weight="bold" />}
            onClick={handleClose}
            aria-label="Close modal"
            size={buttonSize}
            colorScheme="gray"
            variant="ghost"
            borderRadius="full"
          />
        </Flex>

        <ModalBody 
          p={0} 
          display="flex" 
          flexDirection={modalLayout}
          position="relative" 
          mt="60px" 
          overflow="auto"
        >
          {/* Image Section */}
          <Box 
            flex="1"
            position="relative"
            overflow="auto"
            ref={containerRef}
            bg={modalBg}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {isLoading && (
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                zIndex={2}
              />
            )}

            <Box
              data-testid="zoom-pan-container"
              position="relative"
              display="inline-block"
              style={{
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                transition: isDragging ? "none" : "transform 0.2s",
                userSelect: 'none'
              }}
              onMouseDown={handleMouseDown}
              cursor={isDragging ? "grabbing" : scale > 1 ? "grab" : "default"}
            >
              <LazyImage
                ref={imageRef}
                key={imageUrl} 
                src={imageUrl}
                alt="Full size preview"
                style={{
                  objectFit: "contain",
                  WebkitUserDrag: 'none',
                  userSelect: 'none',
                  display: 'block',
                  opacity: isLoading ? 0 : 1,
                  transition: "opacity 0.3s ease-in-out"
                }}
                onLoad={handleImageLoad}
                threshold={0}
                draggable={false}
              />

              {normalizedPredictions.length > 0 && showBoundingBoxes && !isLoading && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  pointerEvents="none"
                >
                  <DetectionBoxes
                    detections={normalizedPredictions}
                    showBoundingBoxes={true}
                  />
                </Box>
              )}
            </Box>

            {hasMultiple && (
              <>
                <IconButton
                  icon={<CaretLeft weight="bold" size={24} />}
                  position="absolute"
                  left={4}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={() => {
                    setScale(1);
                    setPosition({ x: 0, y: 0 });
                    requestAnimationFrame(() => {
                      onPrevious();
                    });
                  }}
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ 
                    bg: "blackAlpha.800", 
                    transform: "translateY(-50%) scale(1.1)" 
                  }}
                  _active={{ 
                    bg: "blackAlpha.900", 
                    transform: "translateY(-50%) scale(0.9)" 
                  }}
                  size={navButtonSize}
                  isRound
                  aria-label="Previous image"
                  zIndex={3}
                  opacity={0.3}
                  _groupHover={{ opacity: 0.8 }}
                  transition="all 0.2s ease-in-out"
                  backdropFilter="blur(4px)"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                />
                <IconButton
                  icon={<CaretRight weight="bold" size={24} />}
                  position="absolute"
                  right={4}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={() => {
                    setScale(1);
                    setPosition({ x: 0, y: 0 });
                    requestAnimationFrame(() => {
                      onNext();
                    });
                  }}
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ 
                    bg: "blackAlpha.800", 
                    transform: "translateY(-50%) scale(1.1)" 
                  }}
                  _active={{ 
                    bg: "blackAlpha.900", 
                    transform: "translateY(-50%) scale(0.9)" 
                  }}
                  size={navButtonSize}
                  isRound
                  aria-label="Next image"
                  zIndex={3}
                  opacity={0.3}
                  _groupHover={{ opacity: 0.8 }}
                  transition="all 0.2s ease-in-out"
                  backdropFilter="blur(4px)"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                />

                {/* Left click zone for navigation */}
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  bottom={0}
                  width="15%"
                  cursor="pointer"
                  zIndex={2}
                  onClick={() => {
                    setScale(1);
                    setPosition({ x: 0, y: 0 });
                    requestAnimationFrame(() => {
                      onPrevious();
                    });
                  }}
                  _hover={{
                    bg: 'blackAlpha.100'
                  }}
                  transition="background 0.2s ease-in-out"
                />

                {/* Right click zone for navigation */}
                <Box
                  position="absolute"
                  right={0}
                  top={0}
                  bottom={0}
                  width="15%"
                  cursor="pointer"
                  zIndex={2}
                  onClick={() => {
                    setScale(1);
                    setPosition({ x: 0, y: 0 });
                    requestAnimationFrame(() => {
                      onNext();
                    });
                  }}
                  _hover={{
                    bg: 'blackAlpha.100'
                  }}
                  transition="background 0.2s ease-in-out"
                />
              </>
            )}
          </Box>

          {/* Sidebar Section */}
          <Box
            w={{ base: "100%", md: "300px" }}
            h={{ base: "calc(40vh - 60px)", md: "calc(100vh - 60px)" }}
            bg={sidebarBg}
            p={4}
            overflowY="auto"
            borderLeft={{ base: "none", md: "1px solid" }}
            borderTop={{ base: "1px solid", md: "none" }}
            borderColor={borderColor}
          >
            <VStack align="stretch" spacing={4}>
              {/* Debug Info Section (only show if there are data issues) */}
              {(!currentImage?.id || !currentImage?.siteId) && (
                <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                  <Text fontSize="sm" color="orange.800" fontWeight="bold" mb={1}>
                    Data Issues Detected
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {!currentImage?.siteId && (
                      <Text fontSize="xs" color="orange.700">• Missing Site ID</Text>
                    )}
                    {!currentImage?.id && (
                      <Text fontSize="xs" color="orange.700">• Missing Media ID</Text>
                    )}
                    <Text fontSize="xs" color="orange.600">
                      Feature editing may not work. Try accessing this image from the site page directly.
                    </Text>
                  </VStack>
                </Box>
              )}

              {/* Image Info Section */}
              <Box>
                <Flex align="center" mb={2} gap={2}>
                  <Info size={iconSize} color={accentColor} />
                  <Heading size="sm" color={textColor}>Image Details</Heading>
                </Flex>
                
                <VStack align="stretch" spacing={2}>
                  {imageSize.width > 0 && (
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textColor}>Dimensions:</Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {imageSize.width} × {imageSize.height}
                      </Text>
                    </HStack>
                  )}
                  
                  {currentImage?.type && (
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textColor}>Type:</Text>
                      <Badge colorScheme="blue">
                        {currentImage.type.toUpperCase()}
                      </Badge>
                    </HStack>
                  )}

                  {currentImage?.id && (
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={textColor}>Media ID:</Text>
                      <Text fontSize="xs" fontFamily="mono" color={textColor}>
                        {currentImage.id}
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </Box>

              {/* Features Section */}
              {normalizedPredictions.length > 0 && (
                <Box>
                  <Flex align="center" mb={2} gap={2}>
                    <TagSimple size={iconSize} color={accentColor} />
                    <Heading size="sm" color={textColor}>Features</Heading>
                  </Flex>
                  
                  <VStack align="stretch" spacing={2}>
                      {normalizedPredictions.map((pred, index) => {
                        // Check if pred.class exists before using it
                        if (!pred.class) {
                          console.warn(`Prediction at index ${index} is missing class property:`, pred);
                          return null;
                        }
                        
                        const displayLabel = pred.class.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                        return (
                          <Box
                            key={index}
                            p={3}
                            bg={featureBg}
                            borderRadius="md"
                            color={textColor}
                            transition="all 0.2s"
                            _hover={{ bg: featureBgHover }}
                          >
                            <HStack justify="space-between" align="start">
                              <VStack align="flex-start" spacing={1}>
                                <HStack>
                                  <Badge colorScheme="blue" fontSize="sm">{index + 1}</Badge>
                                  <Text fontWeight="bold">{displayLabel}</Text>
                                </HStack>
                                {pred.confidence && (
                                  <Text fontSize="sm" opacity={0.8}>
                                    Confidence: {Math.round(pred.confidence * 100)}%
                                  </Text>
                                )}
                                {pred.source && (
                                  <Badge 
                                    colorScheme={pred.source === 'auto' ? 'purple' : 'green'} 
                                    size="sm"
                                  >
                                    {pred.source === 'auto' ? 'Auto-detected' : 'Manual'}
                                  </Badge>
                                )}
                              </VStack>
                            </HStack>
                          </Box>
                        );
                      })}
                  </VStack>
                </Box>
              )}

              {/* Location Section */}
              {currentImage?.subLocation && (
                <Box>
                  <Flex align="center" mb={2} gap={2}>
                    <MapPin size={iconSize} color={accentColor} />
                    <Heading size="sm" color={textColor}>Location</Heading>
                  </Flex>
                  
                  <Box p={3} bg={featureBg} borderRadius="md" color={textColor}>
                    <VStack align="stretch" spacing={2}>
                      <Text fontWeight="bold">{currentImage.subLocation.name}</Text>
                      <Text fontSize="sm" opacity={0.8}>
                        Coordinates: {currentImage.subLocation.coordinates.lat.toFixed(6)}, {currentImage.subLocation.coordinates.lng.toFixed(6)}
                      </Text>
                      <Button
                        size="sm"
                        leftIcon={<Icon as={MdLocationPin} />}
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => {
                          const sublocationsUrl = `/site/${currentImage.siteId}/sublocations`;
                          window.open(sublocationsUrl, '_blank');
                        }}
                      >
                        View on Map
                      </Button>
                    </VStack>
                  </Box>
                </Box>
              )}
            </VStack>
          </Box>
        </ModalBody>
      </ModalContent>

      <SubLocationMapModal
        isOpen={showSubLocationModal}
        onClose={() => setShowSubLocationModal(false)}
        siteId={currentImage?.siteId}
        mediaId={currentImage?.id}
        currentSubLocation={currentImage?.subLocation}
        siteLocation={currentImage?.siteLocation}
        onSubLocationSelect={async (subLocationData) => {
          if (!currentImage?.siteId) {
            toast({
              title: "Error",
              description: "Site ID is missing",
              status: "error",
              duration: 3000
            });
            return;
          }
          try {
            // Make sure we have valid coordinates in the correct format
            const coordinates = subLocationData.coordinates;
            if (!coordinates || (typeof coordinates !== 'object') || 
                (coordinates.lat === undefined && coordinates.latitude === undefined)) {
              throw new Error("Invalid coordinates format");
            }
            
            // Normalize to the format the API expects (lat/lng)
            const normalizedCoordinates = {
              lat: coordinates.lat || coordinates.latitude,
              lng: coordinates.lng || coordinates.longitude
            };

            console.log("Saving sublocation with coordinates:", normalizedCoordinates);
            
            const result = await updateMediaSubLocation(
              currentImage.siteId,
              currentImage.id,
              {
                subLocationId: subLocationData?.id,
                coordinates: normalizedCoordinates
              }
            );

            if (result.success) {
              toast({
                title: "Location Updated",
                description: "The image location has been updated successfully",
                status: "success",
                duration: 3000
              });
              // Trigger media update callback
              if (onMediaUpdated) {
                onMediaUpdated();
              }
              setShowSubLocationModal(false);
            } else {
              throw new Error(result.error);
            }
          } catch (error) {
            console.error("Failed to update sublocation:", error);
            toast({
              title: "Error",
              description: error.message,
              status: "error",
              duration: 3000
            });
          }
        }}
      />

      <FeatureTaggingModal
        isOpen={showFeatureTagging}
        onClose={() => setShowFeatureTagging(false)}
        onFeatureSelect={handleFeatureSelect}
        onFeatureBoxesDrawn={handleFeatureBoxesDrawn}
        existingFeatures={currentImage?.predictions?.filter(p => p.source === 'manual').map(p => p.class) || []}
        currentPredictions={currentImage?.predictions || []}
        isLoading={isSaving}
        media={currentImage}
      />
    </Modal>
  );
}
